/// <amd-module name="Artifacts/PackageRecycleBin/components/RecycleBinPackagesTable" />

import * as Res from "Artifacts/PackageRecycleBin/Resources";
import { IDeletedPackageVersion } from "Artifacts/PackageRecycleBin/types/IDeletedPackageVersion";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { IListSelection } from "VSSUI/List";
import {
  ColumnFill,
  ColumnSelect,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface IRecycleBinPackagesTableItem {
  protocol: string;
  name: string;
  version: string;
  deletedDate: Date;
  scheduledDeleteDate: Date;
}

const recycleBinTableColumns = [
  new ColumnSelect(),
  {
    id: "protocol",
    name: Res.RecycleBinTable_TableColumn_Protocol,
    readonly: true,
    renderCell: renderSimpleCell,
    width: 150,
  },
  {
    id: "name",
    name: Res.RecycleBinTable_TableColumn_Name,
    readonly: true,
    renderCell: renderSimpleCell,
    width: 400,
  },
  {
    id: "version",
    name: Res.RecycleBinTable_TableColumn_Version,
    readonly: true,
    renderCell: renderSimpleCell,
    width: 150,
  },
  {
    id: "deletedDate",
    name: Res.RecycleBinTable_TableColumn_DeletedDate,
    readonly: true,
    renderCell: renderDeletedDate,
    width: 150,
  },
  {
    id: "scheduledDeleteDate",
    name: Res.RecycleBinTable_TableColumn_ScheduledDeleteDate,
    readonly: true,
    renderCell: renderScheduledDeleteDate,
    width: 225,
  },
  ColumnFill,
];

export interface IRecycleBinTableProps {
  packageVersions?: IDeletedPackageVersion[];
  selection: IListSelection;
}

const tableShimmer = new Array(10).fill(
  new ObservableValue<IRecycleBinPackagesTableItem | undefined>(undefined),
);

const RecycleBinTable: React.FC<IRecycleBinTableProps> = ({ packageVersions, selection }) => {
  const tableItems = React.useMemo(() => {
    const items = packageVersions
      ? (packageVersions.map((packageDataItem) => ({
          protocol: packageDataItem.protocol,
          name: packageDataItem.name,
          version: packageDataItem.version,
          deletedDate: packageDataItem.deletedDate,
          scheduledDeleteDate: packageDataItem.scheduledPermanentDeleteDate,
        })) as IRecycleBinPackagesTableItem[])
      : tableShimmer;
    return new ArrayItemProvider<IRecycleBinPackagesTableItem | undefined>(items);
  }, [packageVersions]);

  return (
    <Card className="flex-grow bolt-table-card">
      <Table columns={recycleBinTableColumns} itemProvider={tableItems} selection={selection} />
    </Card>
  );
};

function renderDeletedDate(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IRecycleBinPackagesTableItem>,
  tableItem: IRecycleBinPackagesTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Ago date={tableItem.deletedDate} />
    </SimpleTableCell>
  );
}

function renderScheduledDeleteDate(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IRecycleBinPackagesTableItem>,
  tableItem: IRecycleBinPackagesTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      {tableItem.scheduledDeleteDate.toLocaleDateString()}
    </SimpleTableCell>
  );
}

export { RecycleBinTable };
